/**
 * Analytics view — Fintech Premium Edition
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectItem, Spinner, Button } from "@heroui/react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useS2DashboardContext } from "../context/S2DashboardProvider";
import FundPickerBar from "../components/FundPickerBar";
import { sortPeriods } from "../model/utils";
import { api } from "../services/api";

// Palette moderne et vibrante
const PALETTE = [
  "#0f62fe",
  "#24a148",
  "#fa4d56",
  "#8a3ffc",
  "#ff832b",
  "#1192e8",
  "#da1e28",
  "#198038",
  "#a8a8a8",
  "#6929c4",
  "#005d5d",
  "#570408",
  "#2e2e2e",
  "#9e5200",
  "#004d5b",
];

// ─── Utilitaires de formatage ───────────────────────────────────────────────

function parseNum(v: string | undefined | null): number | null {
  if (!v || v === "nan" || v === "" || v === "None") return null;
  const n = parseFloat(
    String(v).replace(/,/g, "").replace(/%/g, "").replace(/x/gi, "").trim(),
  );
  return isNaN(n) ? null : n;
}

const formatCompactYAxis = (tickItem: number) => {
  if (tickItem === 0) return "0";
  if (Math.abs(tickItem) >= 1e9)
    return (tickItem / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  if (Math.abs(tickItem) >= 1e6)
    return (tickItem / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  if (Math.abs(tickItem) >= 1e3)
    return (tickItem / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return tickItem.toString();
};

const formatValue = (v: unknown, unit: string, currency?: string) => {
  if (typeof v !== "number") return String(v ?? "-");
  if (unit === "currency")
    return (
      new Intl.NumberFormat("en-US").format(v) +
      (currency ? " " + currency : "")
    );
  if (unit === "%") return v + "%";
  if (unit === "x") return v + "x";
  return String(v);
};

// ─── Custom Tooltip (Glassmorphism) ─────────────────────────────────────────

const CustomTooltip = ({ active, payload, label, unit, currency }: any) => {
  if (active && payload && payload.length) {
    const sorted = [...payload]
      .filter((p) => p?.value != null && Number.isFinite(Number(p.value)))
      .sort((a, b) => Number(b.value) - Number(a.value));
    return (
      <div className="rounded-xl border border-gray-100 bg-white/90 p-4 shadow-[0_8px_30px_rgb(0,0,0,0.08)] backdrop-blur-md">
        <p className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-500">
          {label}
        </p>
        <div className="flex flex-col gap-2">
          {sorted.map((entry, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-6 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="h-2.5 w-2.5 shrink-0 rounded-full shadow-sm"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="truncate font-medium text-gray-600 max-w-[140px]" title={entry.name}>{entry.name}</span>
              </div>
              <span className="shrink-0 font-bold text-[#001d6c]">
                {formatValue(entry.value, unit, currency)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// ─── Custom Area chart for fund-level ───────────────────────────────────────

interface FundChartProps {
  title: string;
  labels: string[];
  data: (number | null)[];
  color: string;
  unit: "currency" | "%" | "x" | "";
  currency?: string;
}

function FundChart({
  title,
  labels,
  data,
  color,
  unit,
  currency,
}: FundChartProps) {
  const chartData = labels.map((period, i) => ({ period, value: data[i] }));
  const fullTitle =
    unit === "currency" && currency ? `${title} (${currency})` : title;
  const gradientId = `color-${title.replace(/\s/g, "")}`;

  return (
    <div className="flex flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-1 flex items-center gap-2">
        <div
          className="h-2.5 w-2.5 rounded-full"
          style={{ background: color }}
        />
        <h3 className="text-sm font-semibold text-gray-700">{fullTitle}</h3>
      </div>
      <div className="mt-3 flex-1">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 4, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.15} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#f1f5f9"
            />
            <XAxis
              dataKey="period"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 500 }}
              dy={8}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              tickFormatter={formatCompactYAxis}
              width={44}
            />
            <Tooltip
              content={<CustomTooltip unit={unit} currency={currency} />}
              cursor={{
                stroke: color,
                strokeWidth: 1,
                strokeDasharray: "4 4",
                strokeOpacity: 0.4,
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              name={title}
              stroke={color}
              strokeWidth={2.5}
              fillOpacity={1}
              fill={`url(#${gradientId})`}
              dot={{ r: 3.5, fill: "#fff", stroke: color, strokeWidth: 2 }}
              activeDot={{ r: 6, fill: color, stroke: "#fff", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── Spaghetti line chart per asset ─────────────────────────────────────────

interface SpaghettiChartProps {
  title: string;
  labels: string[];
  datasets: {
    name: string;
    color: string;
    data: (number | null)[];
    hidden: boolean;
  }[];
  unit: "currency" | "%" | "x" | "";
  currency?: string;
}

function SpaghettiChart({
  title,
  labels,
  datasets,
  unit,
  currency,
}: SpaghettiChartProps) {
  const chartData = labels.map((period, i) => {
    const obj: Record<string, unknown> = { period };
    datasets.forEach((ds) => {
      obj[ds.name] = ds.hidden ? null : ds.data[i];
    });
    return obj;
  });

  const visible = datasets.filter((ds) => !ds.hidden);

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <h3 className="mb-4 text-sm font-semibold text-gray-700">{title}</h3>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 4, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#f1f5f9"
          />
          <XAxis
            dataKey="period"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 500 }}
            dy={8}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            tickFormatter={formatCompactYAxis}
            width={44}
          />
          <Tooltip
            content={<CustomTooltip unit={unit} currency={currency} />}
            cursor={{
              stroke: "#94a3b8",
              strokeWidth: 1,
              strokeDasharray: "4 4",
              strokeOpacity: 0.5,
            }}
          />
          {visible.map((ds) => (
            <Line
              key={ds.name}
              type="monotone"
              dataKey={ds.name}
              stroke={ds.color}
              strokeWidth={2}
              dot={{ r: 3, fill: "#fff", stroke: ds.color, strokeWidth: 1.5 }}
              activeDot={{
                r: 5,
                fill: ds.color,
                stroke: "#fff",
                strokeWidth: 2,
              }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Investment Timeline ─────────────────────────────────────────────────────

interface TimelineEvent {
  id: string;
  asset: string;
  date: Date;
  type: "Entry" | "Exit";
  color: string;
}

function InvestmentTimeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0)
    return (
      <p className="p-8 text-center text-sm text-gray-400">
        No investment dates detected.
      </p>
    );

  const sorted = [...events].sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );
  const PAD = 90 * 24 * 60 * 60 * 1000;
  const minT = sorted[0].date.getTime() - PAD;
  const maxT = sorted[sorted.length - 1].date.getTime() + PAD;
  const totalMs = maxT - minT;
  const totalDays = totalMs / (1000 * 60 * 60 * 24);
  const W = Math.max(1200, totalDays * 2);

  const lp = (t: number) => (((t - minT) / totalMs) * 100).toFixed(2);

  const minYear = new Date(minT).getFullYear();
  const maxYear = new Date(maxT).getFullYear();
  const yearTicks: number[] = [];
  for (let y = minYear; y <= maxYear; y++) {
    const t = new Date(y, 0, 1).getTime();
    if (t > minT && t < maxT) yearTicks.push(t);
  }

  return (
    <div className="w-full overflow-x-auto pb-4 custom-scrollbar">
      <div style={{ position: "relative", width: W, minWidth: W, height: 380 }}>
        {/* Base line */}
        <div
          style={{
            position: "absolute",
            top: 190,
            left: 0,
            right: 0,
            height: 4,
            background: "#e2e8f0",
            borderRadius: 2,
          }}
        />

        {/* Year ticks */}
        {yearTicks.map((t) => (
          <div key={t}>
            <div
              style={{
                position: "absolute",
                left: `${lp(t)}%`,
                top: 185,
                width: 2,
                height: 14,
                background: "#94a3b8",
                transform: "translateX(-50%)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: `${lp(t)}%`,
                top: 205,
                transform: "translateX(-50%)",
                fontSize: 13,
                fontWeight: 700,
                color: "#94a3b8",
              }}
            >
              {new Date(t).getFullYear()}
            </div>
          </div>
        ))}

        {/* Events */}
        {sorted.map((ev, idx) => {
          const left = `${lp(ev.date.getTime())}%`;
          const isTop = idx % 2 === 0;
          const mc = ev.type === "Entry" ? "#10b981" : "#ef4444";
          const bg = ev.type === "Entry" ? "#d1fae5" : "#fee2e2";
          const fd = ev.date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });

          return (
            <div key={`${ev.id}-${ev.type}`} className="group">
              {/* Dot */}
              <div
                style={{
                  position: "absolute",
                  left,
                  top: 186,
                  width: 14,
                  height: 14,
                  background: mc,
                  border: "3px solid #fff",
                  borderRadius: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 3,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              />
              {/* Stem */}
              <div
                style={{
                  position: "absolute",
                  left,
                  top: isTop ? undefined : 194,
                  bottom: isTop ? 194 : undefined,
                  width: 2,
                  height: 50,
                  background: mc,
                  transform: "translateX(-50%)",
                  zIndex: 2,
                  opacity: 0.5,
                }}
              />
              {/* Card */}
              <div
                className="transition-transform duration-200 ease-out group-hover:-translate-y-1 group-hover:scale-105 group-hover:z-50"
                style={{
                  position: "absolute",
                  left,
                  top: isTop ? undefined : 244,
                  bottom: isTop ? 244 : undefined,
                  transform: "translateX(-50%)",
                  background: "#fff",
                  border: `1px solid #f1f5f9`,
                  borderTop: `4px solid ${mc}`,
                  borderRadius: 8,
                  padding: "12px 16px",
                  boxShadow:
                    "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                  minWidth: 140,
                  maxWidth: 200,
                  textAlign: "center",
                  zIndex: 4,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  {fd}
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: 14,
                    marginBottom: 8,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={ev.asset}
                >
                  {ev.asset}
                </div>
                <span
                  style={{
                    display: "inline-block",
                    padding: "4px 8px",
                    background: bg,
                    color: mc,
                    fontSize: 10,
                    fontWeight: 700,
                    borderRadius: 6,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {ev.type === "Entry" ? "↓ Entry" : "↑ Exit"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

export default function AnalyticsView() {
  const { selectedFundId, selectedFundIds, funds, docMeta } =
    useS2DashboardContext();

  // Analytics is a single-fund view: explicit single selection has priority.
  const activeFundId = selectedFundId ?? selectedFundIds[0];
  const selectedFund = funds.find((f) => f.fund_id === activeFundId) ?? null;
  const docIds = selectedFund?.doc_ids ?? [];

  const { data: analyticsViewData, isLoading } = useQuery({
    queryKey: ["analytics-data", activeFundId],
    queryFn: () => api.getAnalyticsViewData(activeFundId!),
    enabled: !!activeFundId,
    staleTime: 30_000,
  });

  const caByDocId = useMemo(
    () => analyticsViewData?.capital_account ?? {},
    [analyticsViewData],
  );
  const portByDocId = useMemo(
    () => analyticsViewData?.portfolio ?? {},
    [analyticsViewData],
  );
  const investments = useMemo(
    () => analyticsViewData?.investments ?? [],
    [analyticsViewData],
  );

  const { periodToDocId, sortedPeriods } = useMemo(() => {
    const map: Record<string, string> = {};
    for (const docId of docIds) {
      const meta = docMeta[docId];
      if (!meta) continue;
      const rp = meta.reporting_period || docId;
      if (!map[rp]) map[rp] = docId;
    }
    const sorted = sortPeriods(Object.keys(map));
    return { periodToDocId: map, sortedPeriods: sorted };
  }, [docIds, docMeta]);

  const { caLabels, dataSeries, currency } = useMemo(() => {
    const labels = sortedPeriods;
    const series = {
      Committed: [] as (number | null)[],
      Unfunded: [] as (number | null)[],
      NAV: [] as (number | null)[],
      Distributions: [] as (number | null)[],
      IRR: [] as (number | null)[],
      TVPI: [] as (number | null)[],
      Holdings: [] as (number | null)[],
    };
    let curr = "";

    labels.forEach((rp) => {
      const docId = periodToDocId[rp];
      const caRows = caByDocId[docId] ?? [];
      const row = caRows[0] ?? {};
      series.Committed.push(
        parseNum(row["since_inception_total_commitment_fund_a"]),
      );
      series.Unfunded.push(
        parseNum(row["since_inception_unfunded_commitment_fund_a"]),
      );
      series.NAV.push(parseNum(row["since_inception_nav_after_carry_fund_a"]));
      series.Distributions.push(
        parseNum(row["since_inception_distributions_total_fund_a"]),
      );
      series.IRR.push(parseNum(row["since_inception_net_irr_pct_fund_val"]));
      series.TVPI.push(parseNum(row["since_inception_tvpi_fund_val"]));

      const hMeta = docMeta[docId];
      series.Holdings.push(
        hMeta?.active_holdings != null ? Number(hMeta.active_holdings) : null,
      );
      if (!curr) {
        const c =
          row["since_inception_total_commitment_fund_curr"] ||
          row["system_identifiers_currency"] ||
          "";
        if (c && c !== "nan") curr = c;
      }
    });

    return { caLabels: labels, dataSeries: series, currency: curr };
  }, [sortedPeriods, periodToDocId, caByDocId, docMeta]);

  const portPeriods = useMemo(
    () =>
      sortPeriods(
        docIds.slice().map((id) => docMeta[id]?.reporting_period ?? id),
      )
        .map(
          (rp) =>
            docIds.find((id) => (docMeta[id]?.reporting_period ?? id) === rp)!,
        )
        .filter(Boolean),
    [docIds, docMeta],
  );
  const portLabels = portPeriods.map((p) => docMeta[p]?.reporting_period ?? p);

  const { assetMap } = useMemo(() => {
    const map: Record<string, { name: string; color: string }> = {};
    portPeriods.forEach((docId) => {
      const rows = portByDocId[docId] ?? [];
      rows.forEach((row) => {
        const id = row["portfolio_review_system_identifiers_inv_id"] ?? "";
        if (!id || id === "nan") return;
        if (!map[id]) {
          const name =
            row[
              "portfolio_review_general_information_company_name_short_name_val"
            ] ||
            row[
              "portfolio_review_general_information_company_name_long_name_val"
            ] ||
            id;
          map[id] = {
            name: name !== "nan" ? name : id,
            color: PALETTE[Object.keys(map).length % PALETTE.length],
          };
        }
      });
    });
    return { assetMap: map };
  }, [portPeriods, portByDocId]);

  const [hiddenAssets, setHiddenAssets] = useState<Set<string>>(new Set());
  const toggleAsset = (id: string) =>
    setHiddenAssets((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  const toggleAll = (visible: boolean) =>
    setHiddenAssets(visible ? new Set() : new Set(Object.keys(assetMap)));

  // Reset hidden assets and region period when fund changes
  useEffect(() => {
    setHiddenAssets(new Set());
  }, [activeFundId]);

  const { datasetsFMV, datasetsIRR, datasetsMOIC } = useMemo(() => {
    const fmv: SpaghettiChartProps["datasets"] = [];
    const irr: SpaghettiChartProps["datasets"] = [];
    const moic: SpaghettiChartProps["datasets"] = [];

    Object.entries(assetMap).forEach(([id, info]) => {
      const fmvD = portLabels.map((_, i) => {
        const rows = portByDocId[portPeriods[i]] ?? [];
        const row = rows.find(
          (r) => r["portfolio_review_system_identifiers_inv_id"] === id,
        );
        return row
          ? parseNum(row["portfolio_review_portfolio_positions_fair_value_a"])
          : null;
      });
      const irrD = portLabels.map((_, i) => {
        const rows = portByDocId[portPeriods[i]] ?? [];
        const row = rows.find(
          (r) => r["portfolio_review_system_identifiers_inv_id"] === id,
        );
        return row
          ? parseNum(row["portfolio_review_portfolio_positions_irr_pct_val"])
          : null;
      });
      const moicD = portLabels.map((_, i) => {
        const rows = portByDocId[portPeriods[i]] ?? [];
        const row = rows.find(
          (r) => r["portfolio_review_system_identifiers_inv_id"] === id,
        );
        return row
          ? parseNum(row["portfolio_review_portfolio_positions_moic_mult_val"])
          : null;
      });
      const ds = {
        name: info.name,
        color: info.color,
        hidden: hiddenAssets.has(id),
      };
      fmv.push({ ...ds, data: fmvD });
      irr.push({ ...ds, data: irrD });
      moic.push({ ...ds, data: moicD });
    });
    return { datasetsFMV: fmv, datasetsIRR: irr, datasetsMOIC: moic };
  }, [assetMap, portLabels, portPeriods, portByDocId, hiddenAssets]);

  const [regionPeriod, setRegionPeriod] = useState<string>("");
  useEffect(() => {
    if (portPeriods.length > 0)
      setRegionPeriod(portPeriods[portPeriods.length - 1]);
    else setRegionPeriod("");
  }, [activeFundId, portPeriods.length]);

  const regionData = useMemo(() => {
    const docId = regionPeriod || portPeriods[portPeriods.length - 1];
    const rows = portByDocId[docId] ?? [];
    const counts: Record<string, number> = {};
    rows.forEach((row) => {
      const c =
        row[
          "portfolio_review_general_information_geography_legal_country_val"
        ] ?? "";
      if (!c || c === "nan" || c === "None") return;
      counts[c] = (counts[c] ?? 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [regionPeriod, portPeriods, portByDocId]);

  const timelineEvents = useMemo(() => {
    const parseDate = (s: string) => {
      if (!s || s === "nan" || s === "None") return null;
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    };
    const events: TimelineEvent[] = [];
    investments.forEach((row) => {
      const id = row["investments_summary_inv_id"] ?? "";
      if (!id || id === "nan") return;
      const info = assetMap[id];
      const name =
        row["investments_summary_standardized_name"] ||
        row["investments_summary_inv_name_in_report"] ||
        info?.name ||
        id;
      const color =
        info?.color ?? PALETTE[Object.keys(assetMap).length % PALETTE.length];

      const entryDate = parseDate(row["investments_summary_actual_entry_date"]);
      const exitDate = parseDate(row["investments_summary_actual_exit_date"]);

      if (entryDate)
        events.push({ id, asset: name, date: entryDate, type: "Entry", color });
      if (exitDate)
        events.push({ id, asset: name, date: exitDate, type: "Exit", color });
    });
    return events;
  }, [investments, assetMap]);

  if (!activeFundId) {
    return (
      <div className="flex flex-col gap-4">
        <FundPickerBar mode="single" />
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      </div>
    );
  }

  if (isLoading)
    return (
      <div className="flex flex-col gap-4">
        <FundPickerBar mode="single" />
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading analytics..." color="primary" />
        </div>
      </div>
    );

  return (
    <div className="flex flex-col gap-10 bg-gray-50/30 p-2">
      <FundPickerBar mode="single" />

      {/* ── INVESTMENT TIMELINE ── */}
      <section>
        <h2 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#001d6c]/60 before:block before:h-px before:w-4 before:bg-[#001d6c]/30">
          Investment Timeline
        </h2>
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <InvestmentTimeline events={timelineEvents} />
        </div>
      </section>

      {/* ── CAPITAL DYNAMICS ── */}
      <section>
        <h2 className="mb-5 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#001d6c]/60 before:block before:h-px before:w-4 before:bg-[#001d6c]/30">
          Capital Dynamics
        </h2>
        <div className="grid grid-cols-2 gap-5 xl:grid-cols-3">
          <FundChart
            title="Total Commitment"
            labels={caLabels}
            data={dataSeries.Committed}
            color="#8a3ffc"
            unit="currency"
            currency={currency}
          />
          <FundChart
            title="Unfunded Commitment"
            labels={caLabels}
            data={dataSeries.Unfunded}
            color="#94a3b8"
            unit="currency"
            currency={currency}
          />
          <FundChart
            title="Net Asset Value"
            labels={caLabels}
            data={dataSeries.NAV}
            color="#0f62fe"
            unit="currency"
            currency={currency}
          />
          <FundChart
            title="Distributions"
            labels={caLabels}
            data={dataSeries.Distributions}
            color="#10b981"
            unit="currency"
            currency={currency}
          />
          <FundChart
            title="Active Holdings"
            labels={caLabels}
            data={dataSeries.Holdings}
            color="#f59e0b"
            unit=""
          />
        </div>
      </section>

      {/* ── PERFORMANCE ── */}
      <section>
        <h2 className="mb-5 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#001d6c]/60 before:block before:h-px before:w-4 before:bg-[#001d6c]/30">
          Performance
        </h2>
        <div className="grid grid-cols-2 gap-5">
          <FundChart
            title="Net IRR"
            labels={caLabels}
            data={dataSeries.IRR}
            color="#e11d48"
            unit="%"
          />
          <FundChart
            title="TVPI"
            labels={caLabels}
            data={dataSeries.TVPI}
            color="#f43f5e"
            unit="x"
          />
        </div>
      </section>

      {/* ── INVESTMENT LEVEL ── */}
      <section>
        <h2 className="mb-5 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#001d6c]/60 before:block before:h-px before:w-4 before:bg-[#001d6c]/30">
          Assets Details
        </h2>
        <div className="flex flex-col xl:flex-row gap-5">
          {/* Charts */}
          <div className="flex flex-1 flex-col gap-5">
            <SpaghettiChart
              title={`Fair Market Value per Asset${currency ? " (" + currency + ")" : ""}`}
              labels={portLabels}
              datasets={datasetsFMV}
              unit="currency"
              currency={currency}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SpaghettiChart
                title="Gross IRR (%)"
                labels={portLabels}
                datasets={datasetsIRR}
                unit="%"
              />
              <SpaghettiChart
                title="Gross MOIC (x)"
                labels={portLabels}
                datasets={datasetsMOIC}
                unit="x"
              />
            </div>
          </div>

          {/* Asset selector panel */}
          <div className="w-full xl:w-56 shrink-0 rounded-xl border border-gray-100 bg-white overflow-hidden flex flex-col shadow-sm">
            <div className="border-b border-gray-100 bg-gray-50/50 p-4">
              <h3 className="text-xs font-bold text-[#001d6c] mb-1 uppercase tracking-wide">
                Compare Assets
              </h3>
              <p className="text-[11px] text-gray-500 mb-3">
                Toggle companies visibility
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="flat"
                  className="flex-1 text-[11px] bg-white"
                  onPress={() => toggleAll(true)}
                >
                  Show All
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  className="flex-1 text-[11px] bg-white"
                  onPress={() => toggleAll(false)}
                >
                  Hide All
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar max-h-[400px] xl:max-h-none">
              {Object.entries(assetMap).map(([id, info]) => (
                <div
                  key={id}
                  onClick={() => toggleAsset(id)}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 transition-all active:scale-95"
                  style={{ opacity: hiddenAssets.has(id) ? 0.4 : 1 }}
                >
                  <div
                    className="h-3 w-3 shrink-0 rounded-full shadow-sm"
                    style={{ background: info.color }}
                  />
                  <span
                    className="truncate text-xs font-semibold text-gray-700"
                    title={info.name}
                  >
                    {info.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── REGION ── */}
      <section>
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-6 flex flex-col gap-3 border-b border-gray-50 pb-4">
              <h3 className="text-sm font-bold text-[#001d6c]">
                🌍 Portfolio by Country
              </h3>
              <Select
                size="sm"
                aria-label="Period"
                className="w-full"
                classNames={{
                  trigger: "bg-gray-50 border-gray-100 shadow-none",
                }}
                selectedKeys={
                  regionPeriod ? new Set([regionPeriod]) : new Set()
                }
                onSelectionChange={(keys) =>
                  setRegionPeriod(Array.from(keys)[0] as string)
                }
              >
                {portPeriods.map((p) => (
                  <SelectItem key={p}>
                    {docMeta[p]?.reporting_period ?? p}
                  </SelectItem>
                ))}
              </Select>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={regionData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="46%"
                  outerRadius={95}
                  innerRadius={58}
                  stroke="none"
                  paddingAngle={3}
                >
                  {regionData.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0];
                    const total = regionData.reduce((s, d) => s + d.value, 0);
                    const pct = total
                      ? Math.round(((p.value as number) / total) * 100)
                      : 0;
                    return (
                      <div
                        style={{
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                          padding: "10px 14px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                          fontSize: 12,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            color: "#001d6c",
                            marginBottom: 4,
                          }}
                        >
                          {p.name}
                        </div>
                        <div style={{ color: "#374151" }}>
                          {p.value} position{(p.value as number) > 1 ? "s" : ""}{" "}
                          <span
                            style={{ color: p.fill as string, fontWeight: 700 }}
                          >
                            ({pct}%)
                          </span>
                        </div>
                      </div>
                    );
                  }}
                />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  wrapperStyle={{
                    fontSize: "11px",
                    paddingTop: "16px",
                    color: "#6b7280",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}
